import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { offWhite } from '@bufferapp/components/style/color';
import { grayLight } from '@bufferapp/ui/style/colors';
import { borderWidth } from '@bufferapp/components/style/border';
import Text from '@bufferapp/ui/Text';
import { WelcomeChecklistContainer } from '~/welcome-checklist/components/WelcomeChecklistContainer';

import LoadingProfileListItem from '../LoadingProfileListItem';
import ProfileListItem from '../ProfileListItem';
import ProfileList from '../ProfileList';
import ProfileSearch from '../ProfileSearch';
import ManageSocialAccounts from '../ManageSocialAccounts';
import SidebarButtons from '../SidebarButtons';

const ProfileSidebarStyle = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  box-sizing: border-box;
  background: ${offWhite};
  border-right: ${borderWidth} solid ${grayLight};
  height: 100%;
  justify-content: flex-start;
`;

const ProfileListStyle = styled.div`
  overflow-y: scroll;
`;

const Wrapper = styled.div``;

const renderLoadingProfiles = () => (
  <Wrapper>
    <LoadingProfileListItem />
    <LoadingProfileListItem offset="100ms" />
    <LoadingProfileListItem offset="200ms" />
    <LoadingProfileListItem offset="300ms" />
    <LoadingProfileListItem offset="400ms" />
  </Wrapper>
);

const EmptyProfileList = () => (
  <Text color="gray" type="p">
    No matches found
  </Text>
);

const ProfileSidebar = ({
  loading,
  selectedProfileId,
  profiles,
  searchText,
  onProfileClick,
  onDropProfile,
  profileLimit,
  onSearchProfileChange,
  // Flags for showing connection shortcut buttons
  canManageSocialAccounts,
  reachedChannelLimit,
  manageChannelsURL,
  ownerEmail,
  // Page buttons
  onContentButtonClick,
  onCampaignsButtonClick,
  onBlueprintsButtonClick,
  onCalendarButtonClick,
  isContentSelected,
  isCampaignsSelected,
  isBlueprintsSelected,
  isCalendarSelected,
  // Feature flags
  hasCampaignsFlip,
}) => {
  const [queueExpanded, setQueueExpanded] = useState(true);

  const toggleQueueExpanded = () => {
    setQueueExpanded(!queueExpanded);
  };

  const filteredProfiles = profiles.filter((profile) => !profile.filtered);

  const [showAddChannels, setShowAddChannels] = useState(true);
  const [showManageChannels, setShowManageChannels] = useState(true);

  return (
    <ProfileSidebarStyle data-testid="channel-list-sidebar">
      {loading && renderLoadingProfiles()}
      <ProfileListStyle data-hide-scrollbar>
        <SidebarButtons
          onContentButtonClick={onContentButtonClick}
          isContentSelected={isContentSelected}
          onCalendarButtonClick={onCalendarButtonClick}
          isCalendarSelected={isCalendarSelected}
          onCampaignsButtonClick={onCampaignsButtonClick}
          onBlueprintsButtonClick={onBlueprintsButtonClick}
          isBlueprintsSelected={isBlueprintsSelected}
          isCampaignsSelected={isCampaignsSelected}
          hasCampaignsFlip={hasCampaignsFlip}
          toggleQueueExpanded={toggleQueueExpanded}
          queueExpanded={queueExpanded}
        />

        {queueExpanded && (
          <>
            {profiles.length > 9 && (
              <ProfileSearch
                onSearchProfileChange={onSearchProfileChange}
                searchText={searchText}
              />
            )}
            {filteredProfiles.length > 0 && (
              <ProfileList
                selectedProfileId={selectedProfileId}
                profiles={filteredProfiles}
                onProfileClick={onProfileClick}
                profileLimit={profileLimit}
                onDropProfile={onDropProfile}
              />
            )}
            {!filteredProfiles.length && EmptyProfileList()}
          </>
        )}
      </ProfileListStyle>
      <ManageSocialAccounts
        canManageSocialAccounts={canManageSocialAccounts}
        reachedChannelLimit={reachedChannelLimit}
        manageChannelsURL={manageChannelsURL}
        ownerEmail={ownerEmail}
        hasProfiles={profiles && !!profiles.length}
        showAddChannels={showAddChannels}
        showManageChannels={showManageChannels}
        numProfiles={profiles ? profiles.length : 0}
      />
      <WelcomeChecklistContainer
        setShowAddChannels={setShowAddChannels}
        setShowManageChannels={setShowManageChannels}
      />
    </ProfileSidebarStyle>
  );
};

ProfileSidebar.propTypes = {
  loading: PropTypes.bool,
  onProfileClick: ProfileList.propTypes.onProfileClick,
  manageChannelsURL: PropTypes.string.isRequired,
  selectedProfileId: ProfileList.propTypes.selectedProfileId,
  profiles: PropTypes.arrayOf(PropTypes.shape(ProfileListItem.propTypes)),
  searchText: PropTypes.string,
  profileLimit: PropTypes.number,
  onDropProfile: PropTypes.func,
  onSearchProfileChange: () => {},
  canManageSocialAccounts: PropTypes.bool,
  hasCampaignsFlip: PropTypes.bool,
  isCampaignsSelected: PropTypes.bool,
  onCampaignsButtonClick: PropTypes.func,
  isBlueprintsSelected: PropTypes.bool,
  onBlueprintsButtonClick: PropTypes.func.isRequired,
  ownerEmail: PropTypes.string,
  reachedChannelLimit: PropTypes.bool,
  onCalendarButtonClick: PropTypes.func.isRequired,
  isCalendarSelected: PropTypes.bool,
  onContentButtonClick: PropTypes.func.isRequired,
  isContentSelected: PropTypes.bool,
};

ProfileSidebar.defaultProps = {
  loading: false,
  onProfileClick: ProfileList.defaultProps.onProfileClick,
  selectedProfileId: ProfileList.defaultProps.selectedProfileId,
  profiles: [],
  searchText: '',
  onSearchProfileChange: PropTypes.func,
  onCampaignsButtonClick: () => {},
  profileLimit: 0,
  onDropProfile: () => {},
  canManageSocialAccounts: true,
  hasCampaignsFlip: false,
  isCampaignsSelected: false,
  ownerEmail: 'the owner',
  reachedChannelLimit: false,
  isCalendarSelected: false,
  isContentSelected: false,
  isBlueprintsSelected: false,
};

export default ProfileSidebar;
